// Per-tab status fields. Each service tab owns ONE status column (a plain
// string in row_data, '' = No Status) with its own color system: the table
// pins it under the business name and tints the row's editors in the
// selected status's colors. Tabs without an entry show no status and no
// tint. Account Status (V1's config-driven colors) now belongs to the
// Bookkeeping tab only.

import {
  ACCOUNT_STATUS_OPTIONS,
  type AccountStatusOption,
} from './accountStatus';

export interface TabStatusField {
  /** row_data key (and display name) the status saves to */
  column: string;
  /** First option is the '' / No Status entry */
  options: AccountStatusOption[];
}

const ACCOUNT_CLOSED_OPTION = ACCOUNT_STATUS_OPTIONS.find(
  o => o.value === 'Account Closed'
) as AccountStatusOption;

/** No Status / Pending / Complete / Account Closed — shared by Sales Tax
 *  and Payroll */
const PROGRESS_STATUS_OPTIONS: AccountStatusOption[] = [
  {
    value: '',
    label: 'No Status',
    rowBgColor: '#ffffff',
    rowTextColor: 'inherit',
    highlightColor: '#e5f0fe',
  },
  {
    value: 'Pending',
    label: 'Pending',
    rowBgColor: '#fef3c7',
    rowTextColor: '#92400e',
    highlightColor: '#fde68a',
  },
  {
    value: 'Complete',
    label: 'Complete',
    rowBgColor: '#dcfce7',
    rowTextColor: '#166534',
    highlightColor: '#bbf7d0',
  },
  // Same gray, italic, whole-row tint as Account Status's "Account Closed"
  ACCOUNT_CLOSED_OPTION,
];

export const ACCOUNT_STATUS_FIELD: TabStatusField = {
  column: 'Account Status',
  options: ACCOUNT_STATUS_OPTIONS,
};

export const SALES_TAX_STATUS_FIELD: TabStatusField = {
  column: 'Sales Tax Status',
  options: PROGRESS_STATUS_OPTIONS,
};

export const PAYROLL_STATUS_FIELD: TabStatusField = {
  column: 'Payroll Status',
  options: PROGRESS_STATUS_OPTIONS,
};

const STATUS_FIELD_BY_TAB: Record<string, TabStatusField> = {
  bookkeeping: ACCOUNT_STATUS_FIELD,
  'sales-tax': SALES_TAX_STATUS_FIELD,
  payroll: PAYROLL_STATUS_FIELD,
};

/** Every per-tab status column (pinned/sectioned, never a plain tab column) */
export const TAB_STATUS_COLUMNS = Object.values(STATUS_FIELD_BY_TAB).map(
  f => f.column
);

/** The status field a tab owns, if any */
export function tabStatusField(tabId: string): TabStatusField | undefined {
  return STATUS_FIELD_BY_TAB[tabId];
}

/** The selected option for a raw status value; undefined = No Status
 *  (blank, 'none', or a value the field doesn't define) */
export function getStatusOption(
  field: TabStatusField,
  value: unknown
): AccountStatusOption | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  if (!normalized || normalized.toLowerCase() === 'none') return undefined;
  return field.options.find(o => o.value === normalized);
}
